/**
 * Times as the workspace reads them.
 *
 * Every run is dated in Asia/Dubai — the schedules are, the obligations are,
 * and the person reading the screen is. `toLocaleString()` with no arguments
 * renders the *browser's* locale and zone, so a run at 06:30 Dubai read
 * "2:30:00 AM" on a laptop in London and "9/9/2026" to everyone: a US date
 * order nobody in the workspace uses.
 */
const TZ = "Asia/Dubai";

const DATE_TIME = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TZ,
});

const TIME = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TZ,
});

/** "9 Sep 2026, 12:20" — always Dubai, never the browser's zone. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return DATE_TIME.format(d);
}

/** "12:20" — for rows already grouped under a date. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return TIME.format(d);
}

/** Today in Asia/Dubai as YYYY-MM-DD — the default `as_of` for a new run. */
export function todayInWorkspace(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: TZ,
  }).format(new Date());
  return parts;
}

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** "2026-09-26" → "26 Sep 2026": a reporting day, written like every other date. */
export function formatDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? day : DAY.format(d);
}

/** A subject's value as a person reads it: an ISO day becomes a date; anything else stays. */
export function readableValue(v: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? formatDay(v) : v;
}
